import { useEffect, useRef } from 'react';
import { useAnalytics } from '../../../../../../common/analytics';
import { type LimitedGasPayload , type FiltersForAnalytics } from '../../../../../../common/analytics/events';
import { type ProductStates } from '../../../../../product-state-machine';
import { type SearchItems } from '../../../result-types';
import { type Product, useProductContext } from '../../../..';
import { useDialogExpansionContext } from '../../../../../dialog-expansion-context';
import { preQueryAnalyticsGenerator } from './pre-query-analytics-generator';
import { type CommonAttributes } from './product-state-analytics-types';
import { postQueryAnalyticsGenerator } from './post-query-analytics-generator';
import { useGetCurrentProductFilters } from '../../../../../filters/filters-helper-functions';
import { transformFiltersForAnalytics } from './filters-context-transformer';
import { errorAnalyticsGenerator } from './error-analytics-generator';

export const useProductStateAnalytics = (
	productState: ProductStates,
	preQueryItems: SearchItems,
	postQueryItems: SearchItems,
) => {
	useCommonConditions(productState, preQueryAnalyticsGenerator, preQueryItems);
	useCommonConditions(productState, postQueryAnalyticsGenerator, postQueryItems);
	useCommonConditions(productState, errorAnalyticsGenerator, {
		sections: [],
		size: 0,
	});
};

export const useCommonContext = (): [
	(payload: LimitedGasPayload) => void,
	Product | undefined,
	boolean,
	boolean,
	FiltersForAnalytics,
] => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { products, getActiveProduct } = useProductContext();
	const filters = useGetCurrentProductFilters();
	const { isExpanded } = useDialogExpansionContext();
	const activeProduct = getActiveProduct();
	const isMultiProduct = products.length > 0;

	return [
		fireAnalyticsEvent,
		activeProduct,
		isExpanded,
		isMultiProduct,
		transformFiltersForAnalytics(filters),
	];
};

export type AnalyticsGenerator = (
	productState: ProductStates,
	commonAttributes: CommonAttributes,
	previousState: ProductStates,
	searchItems: SearchItems,
) => LimitedGasPayload | undefined;

// for testing only
export const useCommonConditions = (
	productState: ProductStates,
	analyticsGenerator: AnalyticsGenerator,
	searchItems: SearchItems,
) => {
	const previousStateOfStateMachine = useRef<ProductStates>();
	const [fireAnalyticsEvent, activeProduct, isExpanded, isMultiProduct, filters] =
		useCommonContext();

	useEffect(() => {
		if (
			isExpanded &&
			activeProduct &&
			activeProduct.id !== '' &&
			activeProduct.isDisplayed &&
			previousStateOfStateMachine.current
		) {
			const commonAttributes = {
				activeProduct: activeProduct?.id,
				isMultiProduct,
				filters,
			};
			const payload = analyticsGenerator(
				productState,
				commonAttributes,
				previousStateOfStateMachine.current,
				searchItems,
			);
			if (payload && Object.keys(payload).length > 0) {
				fireAnalyticsEvent(payload);
			}
		}
		previousStateOfStateMachine.current = productState;
	}, [
		productState,
		isExpanded,
		fireAnalyticsEvent,
		isMultiProduct,
		activeProduct,
		searchItems,
		analyticsGenerator,
		filters,
	]);
};
